import * as React from 'react';
import { useSelector } from 'react-redux';

import { useApplicationContext } from 'shared/containers/ApplicationContext';
import type { INewbuildingInfoForTgbSchema } from 'shared/repositories/newbuilding-search/entities/get_newbuildings_info_for_tgb/NewbuildingInfoForTgbSchema';
import { selectIsNewbuildingCpmFormatInCardExperimentGroupA } from 'shared/selectors/abExperiments/selectNewbuildingCpmFormatInCard';
import { fetchGetNewbuildingsForSerpService } from 'shared/services/fetchGetNewbuildingsForSerpService';

import {
  TgbNativedescGeneral as TgbNativedescGeneralA,
  TgbNativedescGeneralProps as TgbNativedescGeneralPropsA,
} from './TgbNativedescGeneralA';
import {
  TgbNativedescGeneral as TgbNativedescGeneralB,
  TgbNativedescGeneralProps as TgbNativedescGeneralPropsB,
} from './TgbNativedescGeneralB';

export type TgbNativedescGeneralContext = {
  type: 'tgb_nativedesc_general';
  url: string;
  image: string;
  logoImageUrl?: string;
  title: string;
  description: string;
  metro?: string;
  metroColor?: string;
  metroDistance?: string;
  location: string;
  phone?: string;
  site: string;
  builderName: string;
  annotation: string;
  button: string;
  label?: string;
  bannerId: string;
  newBuildingId?: string;
  newBuildingIdIsUsed?: '1';
};

type TgbNativedescGeneralContainerProps = {
  context: TgbNativedescGeneralContext;
};

export const TgbNativedescGeneralContainer: React.FC<TgbNativedescGeneralContainerProps> = props => {
  const { context } = props;

  const { annotation, image, url, title, label, logoImageUrl, newBuildingId, newBuildingIdIsUsed, description } =
    context;

  const applicationContext = useApplicationContext();

  const isNewbuildingCpmFormatInCardExperimentGroupA = useSelector(selectIsNewbuildingCpmFormatInCardExperimentGroupA);

  /**
   * @description Нам не нужно хранить в сторе данные по баннеру,
   * так как страница сама по себе не перерисовывается с/без баннера + баннер нельзя закрыть и открыть
   */
  const [newbuidingInfoForTgb, setNewbuidingInfoForTgb] = React.useState<null | INewbuildingInfoForTgbSchema>(null);
  const isFetching = React.useRef(false);

  React.useEffect(() => {
    if (!isFetching.current && newBuildingIdIsUsed && newBuildingId && !newbuidingInfoForTgb) {
      isFetching.current = true;

      fetchGetNewbuildingsForSerpService(applicationContext, { newbuildingIds: [parseInt(newBuildingId, 10)] })
        .then(data => {
          setNewbuidingInfoForTgb(data.newbuildings[0]);
        })
        .finally(() => {
          isFetching.current = false;
        });
    }
    /**
     * @description Тут смотрим на изменение newBuildingId, изменение остальных данных не должно триггерить запрос
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newBuildingId]);

  /**
   * @todo Удалить логику с экспериментом
   * @description Данный функционал появился в задаче CD-199718, будет удалён в задаче CD-199865
   * Тут нужно удалить один из возвращаемых типов
   */
  const tgbNativedescGeneralProps = ((): (TgbNativedescGeneralPropsA | TgbNativedescGeneralPropsB) | null => {
    if (newBuildingIdIsUsed) {
      if (newbuidingInfoForTgb) {
        return {
          logoUrl: newbuidingInfoForTgb.publisher.logo.url,
          url,
          label,
          image,
          annotation,
          title,
          description,
        };
      }

      return null;
    }

    return {
      logoUrl: logoImageUrl,
      url,
      label,
      image,
      annotation,
      title,
      description,
    };
  })();

  if (tgbNativedescGeneralProps) {
    return isNewbuildingCpmFormatInCardExperimentGroupA ? (
      <TgbNativedescGeneralA {...tgbNativedescGeneralProps} />
    ) : (
      <TgbNativedescGeneralB {...tgbNativedescGeneralProps} />
    );
  }

  return null;
};
